// mic-permission — is the agent's microphone usable, decided without React.
//
// A desk with a blocked microphone used to read "Ready for the next call":
// nothing asked the browser about the mic until a call was already being set
// up, and the one message that path produced (useSip's "Microphone blocked")
// was set on `error`, which no screen rendered. So an agent could sit in the
// queue, take a customer, and be silent on the line (TC-A01.2).
//
// The decisions live here so they can be tested without a browser; useSip
// owns the side effects (permissions.query, getUserMedia).

/** What the desk knows about the microphone.
 *  - "unknown": not asked yet, or the browser cannot say (no Permissions API).
 *  - "granted": the browser will hand over audio.
 *  - "blocked": the agent (or a policy) refused it, or there is no input device. */
export type MicStatus = "unknown" | "granted" | "blocked";

/** Map a PermissionState from navigator.permissions.query to a MicStatus.
 *  "prompt" is not an answer yet, so it stays unknown — the desk asks once it
 *  is signed in, and the prompt's result comes back through getUserMedia. */
export function micStatusFromPermission(state: string | null | undefined): MicStatus {
  if (state === "granted") return "granted";
  if (state === "denied") return "blocked";
  return "unknown";
}

/** Does this getUserMedia / sip.js failure mean "no usable microphone"?
 *
 *  Browsers report a refusal as a DOMException named NotAllowedError (Chrome,
 *  Firefox) or SecurityError (older Safari), and a missing device as
 *  NotFoundError. sip.js sometimes rewraps the error, losing the name, so the
 *  message is checked as well. */
export function isMicUnavailableError(err: unknown): boolean {
  const name = (err as { name?: unknown } | null)?.name;
  if (name === "NotAllowedError" || name === "SecurityError" || name === "NotFoundError"
    || name === "PermissionDeniedError" || name === "DevicesNotFoundError") return true;
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /permission|notallowed|denied|getusermedia|requested device not found/i.test(msg);
}

/** The sentence the desk shows while the microphone is blocked. One place, so
 *  the banner and a failed call say the same thing. */
export const MIC_BLOCKED_MESSAGE =
  "Microphone blocked — callers will not hear you. Allow the microphone for this site in the browser's address bar, then reload.";
